// Import utilities for the api.
import Main from './Components/Main';
import { g, l, Shutdown } from './Components/Utils';
import {
	addScript,
	appendToHead,
	currentTime,
	debugEnabled,
	devEnabled,
	getQueryArg,
	getUrl,
	hasProp,
	parseUrl,
	startsWith,
	trigger,
} from './Components/Utils/Helpers';

/**
 * The main api class that constructs all other components.
 *
 * @since 5.0.0
 */
export default class Api {
	/**
	 * "Class" constructor in ES6.
	 *
	 * @since 5.0.0
	 */
	constructor() {
		// If any incompatible environment errors are detected, we need to shutdown now.
		new Shutdown();

		let script = document.currentScript;
		let dev = hasProp(window, '_omdev') ? window._omdev : false;
		let embed = script
			? script.getAttribute('data-account') || script.getAttribute('data-campaign') || (dev && dev.embed)
			: null;

		// Set some default variables for the class.
		this.script = (dev && dev[embed] ? dev[embed].script : null) || script;
		this.data = (dev && dev[embed] ? dev[embed].data : null) || {
			user: this.script.getAttribute('data-user') || null,
			account: this.script.getAttribute('data-account') || null,
			campaign: this.script.getAttribute('data-campaign') || null,
			env: this.script.getAttribute('data-env') || 'production',
			script: this.script,
			oldEmbed: true,
			pageStart: currentTime(),
			debug: debugEnabled(),
			ruleTest: getQueryArg('om-live-rules-preview'),
			cname: !startsWith(parseUrl(this.script.src).host, 'a.')
				? {
						domain: parseUrl(this.script.src),
						cdn: !startsWith(parseUrl(this.script.src).pathname, '/a/'),
						api: this.script.getAttribute('data-api') || null,
					}
				: false,
		};

		this.preInit(dev, embed);
	}

	/**
	 * Runs the rest of the code to complete the pre-init setup.
	 *
	 * @param {boolean} dev    Whether or not we're in dev mode.
	 * @param {string}  embed  The embed account id or campaign slug.
	 *
	 * @returns {void}
	 */
	preInit(dev, embed) {
		// Set up the `window._omapp` object.
		this.setupOmAppObject();

		// Load up our debug script if its set as a query arg.
		if (this.data.debug) {
			if (!document.getElementById('om-api-debug')) {
				addScript(getUrl(this.data, 'debug', '?' + currentTime())).then((s) => (s.id = 'om-api-debug'));
			}
			window._omdebug = true;
		}

		// Load the Api.js CSS file.
		if (!document.getElementById('omapi-css')) {
			new Promise((resolve, reject) => {
				const style = document.createElement('link');
				style.rel = 'stylesheet';
				style.href = getUrl(this.data, 'css');
				style.id = 'omapi-css';
				style.media = 'all';
				style.onload = resolve;
				style.onerror = reject;

				// Attempt to append it to the <head>, otherwise append to the document.
				// eslint-disable-next-line
				appendToHead(style)
			})
				.then(() => {
					window[g.GLOBAL_OM].cssLoaded = true;
				})
				.catch(() => {
					l.debug('Global Campaign CSS could not be loaded.');
				});
		}

		// Load up our Live Site Inspector script if its set as a query arg.
		if (this.data.ruleTest && !window._omRuleTest) {
			addScript(getUrl(this.data, 'lsi', '?' + currentTime()));
			window._omRuleTest = true;
		}

		// Load our dev url if set with a query arg.
		if (devEnabled() && !dev[embed] && this.data.user && (this.data.account || this.data.campaign)) {
			// Force the environment to dev so we can load the proper script.
			addScript(
				getUrl(
					{
						...this.data,
						env: 'dev',
					},
					'api',
					'?omdev=true'
				),
				true,
				null,
				null,
				null,
				(s) => {
					s.dataset.user = this.data.user;

					if (this.data.campaign) {
						s.dataset.campaign = this.data.campaign;
					} else if (this.data.account) {
						s.dataset.account = this.data.account;
					}

					return s;
				}
			);

			if ('object' !== typeof window._omdev) {
				window._omdev = {};
			}

			window._omdev[embed] = {
				script: this.script,
				data: this.data,
			};

			return;
		}

		// Initialize!
		this.init();
	}

	/**
	 * A default method to initialize the class.
	 *
	 * @since 5.0.0
	 *
	 * @returns {void}
	 */
	init() {
		trigger(document, 'Api.init');

		// Set up the global here for the old embed codes. This can be removed
		// when we're ready to end support for the old embed codes.
		window.OptinMonsterApp = Main;
		window.om_loaded = true;

		// If this is the new embed code, we need some additional magic.
		if ('omapi-script' !== this.script.getAttribute('id')) {
			this.parseAttributes();
			if (!window[this.data.embed]) {
				window[this.data.embed] = new Main();
				window[this.data.embed].init(this.data);
			}
		}
	}

	/**
	 * This parses the data attributes retrieved from the script tag.
	 * This is for the new embed code only.
	 *
	 * @since 5.0.0
	 *
	 * @returns {void}
	 */
	parseAttributes = () => {
		// If a user attribute is missing, let's return early.
		if (!this.data.user) {
			throw 'A user attribute is required in the embed code.';
		}

		// We now know this is the new embed code.
		this.data.oldEmbed = false;

		// Set up some defaults that are needed from the attributes.
		if (this.data.account) {
			this.data.a = this.data.user;
			this.data.embed = 'om' + this.data.account + '_' + this.data.user;
		} else if (this.data.campaign) {
			this.data.u = this.data.user + '.' + this.data.campaign;
			this.data.embed = this.data.campaign;
		}
	};

	/**
	 * Set up the `window._omapp` object.
	 *
	 * @returns {void}
	 */
	setupOmAppObject = () => {
		// Create our global variable if needed.
		window[g.GLOBAL_OM] = window[g.GLOBAL_OM] || {
			sessions: false,
			campaigns: {}, // Any campaign that is instantiated will be referenced here.
			custom: {}, // This is used for Dynamic Text Replacement.
			scripts: {}, // Any scripts that need to be loaded for the campaigns.
			active: {
				// Active campaigns will be set here by type.
				popup: [],
				fullscreen: [],
				slide: [],
				inline: [],
				floating: [],
			},
			reset: () => {},
			domains: {},
			cssLoaded: false,
			visitsAdded: false,
		};
	};
}

// Everything happens inside a try/catch block
// so we can properly display errors in the console.
try {
	new Api();
} catch (error) {
	// We are manually setting the error to the OptinMonsterApp global
	// So we can easily retrieve it if necessary and also so that the
	// legacy embed codes don't continue to try and instantiate themselves. This
	// can be removed when we're ready to end support for the old embed codes.
	window.OptinMonsterApp = error;

	// Let's log the error to the console.
	l[hasProp(error, 'type') && 'debug' === error.type ? 'debug' : 'error'](error);
}
